import {
    Grid,
    OrthographicCamera,
    PerspectiveCamera,
} from '@react-three/drei';
import { Center, Spinner } from '@chakra-ui/react';
import * as THREE from 'three';
import { useCallback, useLayoutEffect } from 'react';
import { useAppStore } from '../state/store';
import { useSketchStore } from '../state/useSketchStore';
import { useContextMenuStore } from '../state/useContextMenuStore';
import { shouldRightClickComplete } from '../utils/commandCompletion';
import { buildSketchContextMenu } from '../config/sketchContextMenu';
import { EngineViewport } from './EngineViewport';
import { SketchSceneContent } from './SketchSceneContent';
import { useViewportThemeColors } from '../hooks/useThemeInk';

/**
 * Sketch workbench viewport (Sketch-style freeform modeling).
 *
 * Configuration shell over the shared EngineViewport, like Viewport.tsx is
 * for CAD/3D: Z-up perspective camera, the shared navigation model (MMB
 * pan, Alt+MMB orbit via EngineViewport's default middleButtonMode='pan'),
 * the classic Sketch look — a light ground canvas that stays light in dark
 * mode too (the sketch edge inks are always dark), red/green/blue axes — and
 * the freeform scene content. Canvas shadows follow the Shadows panel.
 *
 * Right-click opens the SketchUp-style Sketch context menu (built from the
 * live sketch selection); with the "right-click completes command"
 * preference on and a tool active, a plain right-click finishes the tool
 * instead, matching the other workbenches.
 */
export function SketchViewport() {
    const view = useAppStore((s) => s.view);
    const shadowsEnabled = useSketchStore((s) => s.shadows.enabled);
    // The sketch canvas is mode-independent (always the light SketchUp-style
    // ground — the edge inks are dark constants), so the grid reads the
    // -sketch token set instead of following the dark-mode grid.
    const gridColors = useViewportThemeColors('sketch');

    const handleContextMenu = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (shouldRightClickComplete(e.shiftKey)) {
            // Sketch tools commit through clicks, not Enter: finishing means
            // handing the pointer back to Select (no selection wipe — the
            // shared BIM finisher clears the app selection).
            useAppStore.getState().setActiveTool('select');
            return;
        }
        const menu = useContextMenuStore.getState();
        // Cross-workbench references (CAD entities / BIM elements) keep the
        // standard selection menus — the SketchUp-style menu is for sketch
        // geometry. Sketch selection empty + app selection present = foreign.
        const appSelectedIds = useAppStore.getState().selectedIds;
        if (useSketchStore.getState().selectedIds.length === 0 && appSelectedIds.length > 0) {
            menu.show(e.clientX, e.clientY, undefined, appSelectedIds);
            return;
        }
        menu.showCustom(e.clientX, e.clientY, buildSketchContextMenu());
    }, []);

    // viewType is app-global; a BIM plan view left it as 'plan' before the
    // user switched workbenches. Sketch always orbits, so clear the leak
    // pre-paint or OrbitControls would mount rotation-locked. Projection is
    // app-global too — Sketch always mounts in perspective, whatever lens
    // the previous workbench (or a persisted session) left behind. The
    // status-bar ortho toggle still works once inside.
    useLayoutEffect(() => {
        const { view, updateView } = useAppStore.getState();
        if (view.viewType === 'plan' || view.projection === 'orthographic') {
            updateView({
                viewType: view.viewType === 'plan' ? '3d' : view.viewType,
                projection: 'perspective',
            });
        }
    }, []);

    return (
        <EngineViewport
            camera={
                view.viewType === 'plan' ? (
                    <OrthographicCamera
                        makeDefault
                        position={[0, 0, 100]}
                        up={[0, 1, 0]}
                        zoom={20}
                        near={0.1}
                        far={10000}
                        onUpdate={(c) => c.lookAt(0, 0, 0)}
                    />
                ) : view.projection === 'orthographic' ? (
                    // Shared projection toggle (status bar): same BIM pattern —
                    // keep the perspective pose, swap the lens for an ortho one.
                    <OrthographicCamera
                        makeDefault
                        position={view.camera.position}
                        up={[0, 0, 1]}
                        zoom={10}
                        near={0.1}
                        far={10000}
                    />
                ) : (
                    <PerspectiveCamera
                        makeDefault
                        position={view.camera.position}
                        up={[0, 0, 1]} // Z-up world (shared convention)
                        fov={view.camera.fov}
                        near={view.camera.near}
                        far={view.camera.far}
                    />
                )
            }
            lighting={null}
            postProcessing={false}
            environment
            viewCommands
            showViewCube
            shadows={shadowsEnabled}
            // SketchUp-style light ground in BOTH color modes: the sketch
            // edge inks are dark constants, so a dark canvas hides them.
            containerBg="bg.canvas.sketch"
            canvasClassName="bim-viewport__canvas sketch-viewport__canvas"
            onContextMenu={handleContextMenu}
            controls={{
                target: view.camera.target,
                enableRotate: view.viewType !== 'plan',
                mouseButtons: {
                    LEFT: undefined as any, // Selection/tools own the left button
                    MIDDLE: THREE.MOUSE.PAN, // Shared model: MMB pan, Alt+MMB orbit
                    RIGHT: undefined as any, // Context menu
                },
                screenSpacePanning: true,
            }}
            grid={
                // Status bar Grid toggle (shared view flag, like CAD/BIM).
                view.gridVisible && (
                    <Grid
                        args={[100, 100]}
                        cellSize={1}
                        cellThickness={0.5}
                        cellColor={gridColors.gridMajor}
                        sectionSize={10}
                        sectionThickness={1}
                        sectionColor={gridColors.gridSection}
                        fadeDistance={80}
                        infiniteGrid
                        position={[0, 0, 0]}
                        rotation={[Math.PI / 2, 0, 0]} // drei Grid is Y-up; rotate onto the Z-up ground plane
                    />
                )
            }
            axes={
                <group>
                    <axesHelper args={[5]} />
                </group>
            }
            suspenseFallback={
                <Center position="absolute" inset="0" bg="bg.canvas.sketch">
                    <Spinner size="xl" color="sketch.primary" />
                </Center>
            }
        >
            <SketchSceneContent />
        </EngineViewport>
    );
}
